import React, { useCallback, useState } from 'react';
import { Clickable, Expandable, Input, Text, Stack, Checkbox } from '@moda/om';
import { compact } from '@moda/portal-stanchions';
import NextCircleIcon from '@moda/icons/next-circle-32';
import { PageFilterFacetFragment } from '../../generated/types';
import { usePLPParams } from '../../hooks/usePLPParams';
import { useCountry } from '../CountrySelector';
import { PageFilterPanel } from './PageFilterPanel';
import './PageFilterPrice.scss';

type PageFilterPriceProps = {
  saleTags: (PageFilterFacetFragment | null)[] | null;
  mobile?: boolean;
  onClose: () => void;
};

export const PageFilterPrice: React.FC<PageFilterPriceProps> = ({ saleTags, mobile, onClose }) => {
  const { params, clearFilter, changePriceFilter, addFilter } = usePLPParams();
  const { country } = useCountry();

  const [max, setMax] = useState(params?.pricemax?.toString() || '');
  const [min, setMin] = useState(params?.pricemin?.toString() || '');
  const [priceFilterError, setPriceFilterError] = useState('');

  const showSaleFilter =
    params.sale ||
    (compact(saleTags).some(tag => tag.name === '0') &&
      compact(saleTags).some(tag => tag.name !== '0'));

  const validatePriceFilter = useCallback(() => {
    if (Boolean(min) && Boolean(max) && Number(min) > Number(max)) {
      setPriceFilterError('Please ensure min is less than max.');
    } else {
      changePriceFilter(min, max);
      setPriceFilterError('');
    }
  }, [min, max, changePriceFilter]);

  const handleClear = useCallback(() => {
    clearFilter([
      'pricemin',
      'pricemax',
      ...(showSaleFilter ? (['sale', 'saletags'] as const) : [])
    ]);
    setMin('');
    setMax('');
    setPriceFilterError('');
  }, [clearFilter, setMin, setMax, showSaleFilter]);

  const handleSubmit = useCallback(
    (evt: React.FormEvent) => {
      evt.preventDefault();
      validatePriceFilter();
    },
    [validatePriceFilter]
  );

  const content = (
    <form className="PageFilterPrice__form" onSubmit={handleSubmit}>
      <Stack space={2}>
        {showSaleFilter && (
          <Checkbox
            checked={params.sale}
            onChange={event =>
              event.target.checked ? addFilter('sale', 'true') : clearFilter(['sale', 'saletags'])
            }
          >
            On Sale
          </Checkbox>
        )}
        {priceFilterError && (
          <Text treatment="body2" color="code-red">
            {priceFilterError}
          </Text>
        )}
        <Stack direction="horizontal" space={2}>
          <Input
            className="PageFilterPrice__input"
            onChange={setMin}
            label={`${country.currencyCode} min`}
            value={min}
            error={Boolean(priceFilterError)}
            maxLength={8}
          />
          <Text className="PageFilterPrice__text">to</Text>
          <Input
            className="PageFilterPrice__input"
            onChange={setMax}
            label={`${country.currencyCode} max`}
            value={max}
            error={Boolean(priceFilterError)}
            maxLength={8}
          />
          <Clickable
            className="PageFilterPrice__submit"
            type="submit"
            aria-label="Submit price filters"
          >
            <NextCircleIcon />
          </Clickable>
        </Stack>
      </Stack>
    </form>
  );

  if (mobile) {
    return (
      <PageFilterPanel onClose={onClose} title="Price">
        {content}
      </PageFilterPanel>
    );
  }

  return (
    <Expandable className="PageFilterPrice" icon="chevron" name="Price">
      {content}
      <Clickable
        className="PageFilterPrice__clear"
        onClick={handleClear}
        type="button"
        aria-label="Clear prices"
      >
        Clear
      </Clickable>
    </Expandable>
  );
};
